<template>
    <div class="kv-select" :class="[`kv-${size}-select`, `kv-${variant}-select`, `kv-options-${optionAlignment}-alignment`, { open, disabled, 'w-100': fullWidth }]">
        <KvPopup 
            class="kv-select-wrapper" 
            @before-open="onPopupOpen" 
            @before-close="open = false"
        >

            <template #trigger="{ toggle }">
                <KvInput :variant="variant" :size="size" :disabled="disabled" :editable="false" :class="{ 'kv-select-input-trigger': true }" @click="toggle">
                    <template #custom-content>
                        <div class="kv-select-content">
                            <span v-if="!hasValue" class="kv-select-placeholder">{{ resolvedPlaceholder }}</span>

                            <template v-else>
                                <span v-if="!multiple" class="kv-selected">
                                    <slot name="selected" :option="selectedOption">
                                        {{ selectedOptionLabel }}
                                    </slot>
                                </span>

                                <span v-else class="kv-multiple-selected">
                                    <span v-for="opt in selectedOptions" :key="opt[valueField]" class="kv-chip">
                                        <slot name="chip" :option="opt">
                                            {{ opt[labelField] }}
                                        </slot>
                                        <!-- i18n-validator-ignore-next-line: universal close symbol -->
                                        <button class="kv-chip-remove" type="button" @click.stop="removeOption(opt)">×</button>
                                    </span>
                                </span>
                            </template>
                        </div>
                    </template>

                    <template #append>
                        <Icon icon-class="ui-chevron-down" :size="calculatedIconSize" class="kv-select-arrow" />
                    </template>

                </KvInput>
            </template>

            <template #content="{ toggle }">
                <div class="kv-dropdown" :aria-multiselectable="multiple">
                    <div v-if="filterable || loadOptions" class="kv-select-search-container">
                        <KvInput ref="searchInput" type="text" v-model="searchQuery" :variant="variant" class="kv-select-search-input" :placeholder="resolvedSearchPlaceholder" @input="onSearchInput" />
                    </div>
                    <KvScrollbar contentClass="kv-options pr-2 pl-2" ref="optionsList">
                        <div v-if="isLoading" class="kv-loading-state">
                            <slot name="loading">{{ t('inputs-select-loading') }}</slot>
                        </div>
                        <template v-else>
                            <span v-for="(opt, idx) in displayedOptions" :key="opt[valueField] + '-' + idx" :class="{ 'is-selected': isSelected(opt), 'is-disabled': opt.disabled }" class="kv-option" @click.stop="selectOption(opt, toggle)" @mouseenter="highlightIndex = idx" :aria-selected="isSelected(opt).toString()" role="option">
                                <slot name="option" :option="opt">
                                    {{ opt[labelField] }}
                                </slot>
                            </span>
                            <span v-if="displayedOptions.length === 0" class="kv-empty">
                                {{ resolvedEmptyText }}
                            </span>
                        </template>
                    </KvScrollbar>
                </div>
            </template>

        </KvPopup>
    </div>
</template>

<script>
import KvPopup from '../ui/kv-popup.vue';
import KvScrollbar from '../ui/kv-scrollbar.vue';
import KvInput from './kv-input.vue';
import Icon from '../icon/icon.vue';

export default {
    name: "KvSelect",
    model: { prop: "value", event: "input" },
    components: { KvPopup, KvScrollbar, KvInput, Icon },
    props: {
        value: { required: false },
        options: { type: Array, default: () => [] },
        loadOptions: { type: Function, default: null },
        lazy: { type: Boolean, default: false },
        filterable: { type: Boolean, default: false },
        placeholder: { type: String, default: "" },
        searchPlaceholder: { type: String, default: "" },
        emptyText: { type: String, default: "" },
        valueField: { type: String, default: "value" },
        labelField: { type: String, default: "label" },
        multiple: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        size: { type: String, default: "regular" },
        variant: { type: String, default: "tonal" },
        optionAlignment: { type: String, default: "left" },
        fullWidth: { type: Boolean, default: false },
    },
    data() {
        return {
            open: false,
            highlightIndex: -1,
            internalOptions: [],
            isLoading: false,
            searchQuery: "",
            hasLoadedOnce: false,
            debounceTimeout: null
        };
    },
    computed: {
        resolvedPlaceholder() {
            return this.placeholder || this.t("inputs-select-placeholder");
        },
        resolvedSearchPlaceholder() {
            return this.searchPlaceholder || this.t("inputs-select-search_placeholder");
        },
        resolvedEmptyText() {
            return this.emptyText || this.t("inputs-select-empty_text");
        },
        calculatedIconSize() {
            switch (this.size) {
                case "small": return 1;
                case "large": return 4;
                default: return 2;
            }
        },
        hasValue() {
            if (this.multiple) return Array.isArray(this.value) && this.value.length > 0;
            return this.value !== undefined && this.value !== null && this.value !== "";
        },
        currentOptionsSource() {
            return this.loadOptions ? this.internalOptions : this.options;
        },
        displayedOptions() {
            let opts = this.currentOptionsSource;
            const shouldFilterLocally = (!this.loadOptions && this.filterable) || (this.loadOptions && this.lazy);

            if (shouldFilterLocally && this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                opts = opts.filter(o =>
                    String(o[this.labelField]).toLowerCase().includes(query)
                );
            }
            return opts;
        },
        selectedOptions() {
            if (!this.multiple) return [];
            return this.currentOptionsSource.filter((o) => (this.value || []).includes(o[this.valueField]));
        },
        selectedOption() {
            if (this.multiple) return null;
            return this.currentOptionsSource.find((o) => o[this.valueField] === this.value) || null;
        },
        selectedOptionLabel() {
            return this.selectedOption ? this.selectedOption[this.labelField] : "";
        },
    },
    watch: {
        options: {
            handler(newVal) {
                if (!this.loadOptions) {
                    this.internalOptions = newVal;
                }
            },
            immediate: true
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        async onPopupOpen() {
            this.open = true;
            this.$nextTick(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus();
                }
            });

            if (this.loadOptions) {
                if (this.lazy && this.hasLoadedOnce) return;
                this.fetchData("");
            }
        },
        onSearchInput() {
            if (this.loadOptions && !this.lazy) {
                clearTimeout(this.debounceTimeout);
                this.debounceTimeout = setTimeout(() => {
                    this.fetchData(this.searchQuery);
                }, 300);
            }
        },
        async fetchData(query) {
            this.isLoading = true;
            try {
                const results = await this.loadOptions(query);
                this.internalOptions = Array.isArray(results) ? results : [];
                this.hasLoadedOnce = true;
            } catch (error) {
                console.error("KvSelect: Erro", error);
                this.internalOptions = [];
            } finally {
                this.isLoading = false;
            }
        },
        selectOption(opt, toggle) {
            if (opt.disabled) return;
            const val = opt[this.valueField];
            if (this.multiple) {
                const current = Array.isArray(this.value) ? [...this.value] : [];
                const idx = current.indexOf(val);
                if (idx === -1) current.push(val);
                else current.splice(idx, 1);
                this.$emit("input", current);
                this.$emit("change", current);
            } else {
                this.$emit("input", val);
                this.$emit("change", val);
                toggle();
                this.open = false;
            }
        },
        isSelected(opt) {
            const val = opt[this.valueField];
            return this.multiple
                ? Array.isArray(this.value) && this.value.includes(val)
                : val === this.value;
        },
        removeOption(opt) {
            if (!this.multiple) return;
            const current = Array.isArray(this.value) ? [...this.value] : [];
            const idx = current.indexOf(opt[this.valueField]);
            if (idx !== -1) {
                current.splice(idx, 1);
                this.$emit("input", current);
                this.$emit("change", current);
            }
        },
    },
};
</script>

<style lang="scss">
.kv-select {
    display: inline-block;
    width: max-content;
    font-size: 0.9rem;
    font-family: inherit;

    .kv-select-input-trigger {
        &, 
        .kv-input-wrapper, 
        .kv-input-field,
        .kv-input-custom-content {
            cursor: pointer !important;
        }

        .kv-select-arrow {
            transition: transform 0.2s ease;
            color: #6b7280;
        }
    }

    &.open .kv-select-arrow {
        transform: rotate(180deg);
    }

    &.kv-tinted-select .kv-select-input-trigger .kv-select-arrow {
        color: #1D7836;
    }

    .kv-select-content {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        overflow: hidden; 
    }

    .kv-select-placeholder {
        color: #9DA4AE;
        white-space: nowrap;
        user-select: none;
        line-height: 1;
    }
    &.kv-tinted-select .kv-select-placeholder {
        color: #1D7836;
        opacity: 0.7;
    }

    .kv-selected {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
        width: 100%;
        line-height: 1;
    }

    .kv-multiple-selected {
        display: flex;
        flex-wrap: wrap; 
        gap: 4px;
        width: 100%;
        align-items: center;
        
        .kv-chip {
            background: #e5e7eb;
            color: #374151;
            padding: 2px 8px;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 0.85em;
            line-height: 1.2;
            max-width: 100%;

            .kv-chip-remove {
                background: transparent;
                border: 0;
                cursor: pointer;
                font-weight: 700;
                line-height: 1;
                padding: 0;
                color: inherit;
                display: flex;
                align-items: center;
                justify-content: center;
                
                &:hover {
                    color: #000;
                }
            }
        }
    }

    &.kv-small-select .kv-multiple-selected .kv-chip {
        padding: 0px 6px;
        font-size: 0.75em;
        height: 20px;
    }

    .kv-select-wrapper {
        width: 100%;
        
        .kv-popup-content {
            padding: 8px 0;
        }
    }
    
    &.kv-tinted-select .kv-select-wrapper .kv-popup-content {
        border: 1px solid #2AAA4C;
        background-color: #F3FAF4;
        color: #1D7836;

        .kv-option:hover, .kv-option.is-selected {
            background-color: #1D7836;
            color: #F3FAF4;
        }
    }

    &.kv-options-left-alignment .kv-dropdown .kv-options .kv-option { text-align: left; }
    &.kv-options-center-alignment .kv-dropdown .kv-options .kv-option { text-align: center; }

    .kv-select-search-container {
        padding: 0 10px 8px 10px;
        border-bottom: 1px solid #eee;
        margin-bottom: 4px;
        
        .kv-select-search-input { width: 100%; }
    }
    
    &.kv-tinted-select .kv-select-search-container {
        border-bottom-color: #2AAA4C;
    }

    .kv-loading-state {
        padding: 15px;
        text-align: center;
        color: #888;
        font-size: 13px;
    }

    .kv-dropdown {
        .kv-options {
            list-style: none;
            margin: 0;
            max-height: 300px;

            .kv-option {
                padding: 8px 12px;
                cursor: pointer;
                text-align: left;
                transition: background-color 0.1s ease;
                border-radius: 4px;
                margin: 0 4px;

                &:hover {
                    background: #f3f4f6;
                }
                &.is-selected {
                    background: #f0fdf4; 
                    color: #166534;
                    font-weight: 600;
                }
                &.is-disabled {
                    color: #d1d5db;
                    cursor: not-allowed;
                    background: transparent;
                }
            }
            .kv-empty {
                padding: 12px;
                color: #6b7280;
                text-align: center;
                display: block;
                font-size: 0.9em;
            }
        }
    }

    &.kv-small-select .kv-dropdown .kv-options .kv-option {
        padding: 6px 10px;
        font-size: 0.85rem;
    }
    &.kv-large-select .kv-dropdown .kv-options .kv-option {
        padding: 10px 14px;
        font-size: 1rem;
    }
}
</style>